<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  calculatePrice,
  calculatorPlans,
  config,
  decimal,
  integer,
  number,
} from '../config';

const emit = defineEmits(['select']);
const { t } = useI18n();
const planNames = computed(() => ({
  free: t('pricing.free'),
  start: t('pricing.start'),
  growth: t('pricing.growth'),
  pro: t('pricing.pro'),
}));
const planDescriptions = computed(() => ({
  free: t('pricing.descriptions.free'),
  start: t('pricing.descriptions.start'),
  growth: t('pricing.descriptions.growth'),
  pro: t('pricing.descriptions.pro'),
}));
const selectedPlan = ref('growth');
const users = ref(config.plans.growth.users);
const accounts = ref(config.plans.growth.accounts);
const messages = ref(0);
const storage = ref(config.plans.growth.storage);
const plan = computed(() => config.plans[selectedPlan.value]);
const estimate = computed(() =>
  calculatePrice(
    selectedPlan.value,
    users.value,
    accounts.value,
    messages.value,
    storage.value
  )
);
const estimateRows = computed(() => [
  { label: t('pricing.basePrice'), value: number(estimate.value.basePrice) },
  {
    label: `${number(estimate.value.extraUsers)} ${t('pricing.extraUsers')}`,
    value: number(estimate.value.extraUsersCost),
  },
  {
    label: `${number(estimate.value.extraAccounts)} ${t('pricing.extraAccounts')}`,
    value: number(estimate.value.extraAccountsCost),
  },
  {
    label: `${number(messages.value)} ${t('pricing.sentMessages')}`,
    value: number(estimate.value.messagesCost),
  },
  {
    label: `${number(estimate.value.extraStorage)} ${t('pricing.extraStorage')}`,
    value: estimate.value.storageInquiry
      ? t('pricing.storageInquiry')
      : number(estimate.value.extraStorageCost),
  },
]);

function setUsers(value) {
  users.value = Math.max(plan.value.users, integer(value));
}

function setAccounts(value) {
  accounts.value = Math.max(plan.value.accounts, integer(value));
}

function setMessages(value) {
  messages.value = integer(value);
}

function setStorage(value) {
  storage.value = Math.max(plan.value.storage, decimal(value));
}

function stepUsers(amount) {
  setUsers(users.value + amount);
}

function stepAccounts(amount) {
  setAccounts(accounts.value + amount);
}

function stepStorage(amount) {
  setStorage(storage.value + amount);
}

function changePlan(key) {
  selectedPlan.value = key;
  users.value = Math.max(users.value, config.plans[key].users);
  accounts.value = Math.max(accounts.value, config.plans[key].accounts);
  storage.value = Math.max(storage.value, config.plans[key].storage);
}

function selectPlan(key, capacity) {
  emit('select', key, capacity);
}

const capacityFields = computed(() => [
  {
    key: 'users',
    label: t('pricing.usersLabel'),
    hint: t('pricing.usersHint'),
    value: users.value,
    min: plan.value.users,
    step: stepUsers,
    set: setUsers,
    inputmode: 'numeric',
  },
  {
    key: 'accounts',
    label: t('pricing.accountsLabel'),
    hint: t('pricing.accountsHint'),
    value: accounts.value,
    min: plan.value.accounts,
    step: stepAccounts,
    set: setAccounts,
    inputmode: 'numeric',
  },
  {
    key: 'storage',
    label: t('pricing.storageLabel'),
    hint: t('pricing.storageHint'),
    value: storage.value,
    min: plan.value.storage,
    step: stepStorage,
    set: setStorage,
    inputmode: 'decimal',
  },
]);

function requestCapacity() {
  selectPlan(selectedPlan.value, {
    users: users.value,
    accounts: accounts.value,
    messages: messages.value,
    storage: storage.value,
  });
}
</script>

<template>
  <section id="pricing" class="scroll-mt-24 bg-n-slate-2 py-16 md:py-24">
    <div class="mx-auto max-w-7xl px-5 md:px-8">
      <div class="mb-9 flex flex-wrap items-end justify-between gap-5">
        <div class="max-w-2xl">
          <h2
            class="text-3xl font-bold leading-relaxed text-n-slate-12 md:text-4xl md:leading-relaxed"
          >
            {{ t('pricing.title') }}
          </h2>
          <p class="mb-0 mt-3 text-base leading-8 text-n-slate-11">
            {{ t('pricing.description') }}
          </p>
        </div>
        <span
          class="inline-flex items-center gap-2 rounded-lg border border-n-slate-4 bg-white px-4 py-2 text-sm text-n-slate-11"
        >
          <span
            class="i-fluent-calendar-24-regular size-5"
            aria-hidden="true"
          />{{ t('pricing.periodBadge') }}
        </span>
      </div>

      <div
        class="grid gap-px overflow-hidden rounded-2xl border border-n-slate-4 bg-n-slate-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <article
          v-for="(item, key) in config.plans"
          :key="key"
          class="flex min-w-0 flex-col bg-white"
          :aria-labelledby="`pricing_${key}`"
        >
          <div
            class="flex flex-col p-6 pb-5"
            :class="
              key === 'growth' ? 'bg-woot-600 text-white' : 'text-n-slate-12'
            "
          >
            <div class="mb-3 flex min-h-7 items-center justify-between gap-2">
              <h3
                :id="`pricing_${key}`"
                class="mb-0 text-xl font-bold"
                :class="key === 'growth' ? 'text-white' : 'text-n-slate-12'"
              >
                {{ planNames[key] }}
              </h3>
              <span
                v-if="key === 'growth'"
                class="rounded-md bg-white/15 px-2.5 py-1 text-xs font-bold text-white"
              >
                {{ t('pricing.recommended') }}
              </span>
            </div>
            <p
              class="mb-5 min-h-12 text-sm leading-6"
              :class="key === 'growth' ? 'text-white/90' : 'text-n-slate-11'"
            >
              {{ planDescriptions[key] }}
            </p>
            <p
              class="mb-0 whitespace-nowrap text-3xl font-bold leading-relaxed tabular-nums"
              :class="key === 'growth' ? 'text-white' : 'text-n-slate-12'"
            >
              {{ key === 'free' ? t('pricing.freePrice') : number(item.price) }}
            </p>
            <p
              class="mb-0 mt-1 text-xs leading-6"
              :class="key === 'growth' ? 'text-white/90' : 'text-n-slate-11'"
            >
              {{
                key === 'free'
                  ? t('pricing.freePeriod')
                  : t('pricing.pricePeriod')
              }}
            </p>
          </div>
          <div class="flex flex-1 flex-col p-6 pt-5">
            <dl class="mb-6 space-y-4 text-sm text-n-slate-11">
              <div class="flex items-center justify-between gap-3">
                <dt class="flex items-center gap-2">
                  <span
                    class="i-fluent-people-24-regular size-4 shrink-0"
                    aria-hidden="true"
                  />{{ t('pricing.users') }}
                </dt>
                <dd class="m-0 font-bold tabular-nums text-n-slate-12">
                  {{ number(item.users) }}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-3">
                <dt class="flex items-center gap-2">
                  <span
                    class="i-fluent-chat-multiple-24-regular size-4 shrink-0"
                    aria-hidden="true"
                  />{{ t('pricing.accounts') }}
                </dt>
                <dd class="m-0 font-bold tabular-nums text-n-slate-12">
                  {{ number(item.accounts) }}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-3">
                <dt class="flex items-center gap-2">
                  <span
                    class="i-fluent-folder-24-regular size-4 shrink-0"
                    aria-hidden="true"
                  />{{ t('pricing.fileSpace') }}
                </dt>
                <dd
                  class="m-0 whitespace-nowrap font-bold tabular-nums text-n-slate-12"
                >
                  {{
                    t('pricing.storageValue', { count: number(item.storage) })
                  }}
                </dd>
              </div>
            </dl>
            <p
              class="mb-6 border-t border-n-slate-4 pt-4 text-xs leading-6 text-n-slate-11"
            >
              {{
                key === 'free'
                  ? t('pricing.includedMessages', {
                      count: number(item.messages),
                    })
                  : t('pricing.messageRate', {
                      amount: number(config.sent_message_price),
                    })
              }}
            </p>
            <a
              v-if="key === 'free'"
              href="/app/auth/signup?locale=fa"
              class="mt-auto flex min-h-12 items-center justify-center rounded-lg border border-n-slate-5 px-4 py-3 text-sm font-bold text-n-slate-12 transition-colors hover:bg-n-slate-2 focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4"
            >
              {{ t('pricing.freeAction') }}
            </a>
            <button
              v-else
              class="mt-auto min-h-12 rounded-lg px-4 py-3 text-sm font-bold transition-colors focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4"
              :class="
                key === 'growth'
                  ? 'bg-woot-600 text-white hover:bg-woot-700'
                  : 'bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4'
              "
              @click="
                selectPlan(key, {
                  users: item.users,
                  accounts: item.accounts,
                  messages: 0,
                  storage: item.storage,
                })
              "
            >
              {{ t('pricing.selectNamedPlan', { plan: planNames[key] }) }}
            </button>
          </div>
        </article>
      </div>
      <div
        class="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs leading-6 text-n-slate-11"
      >
        <span class="flex items-center gap-2">
          <span
            class="i-fluent-checkmark-circle-24-regular size-4 text-woot-600"
            aria-hidden="true"
          />{{ t('pricing.sharedFeatures') }}
        </span>
        <span>{{ t('pricing.ai') }} </span>
      </div>

      <div
        class="my-8 flex flex-col justify-between gap-4 border-y border-n-slate-4 py-6 md:flex-row md:items-center"
      >
        <div>
          <h3 class="text-lg font-bold text-n-slate-12">
            {{ t('pricing.enterpriseTitle') }}
          </h3>
          <p class="mb-0 mt-2 text-sm leading-7 text-n-slate-11">
            {{ t('pricing.enterpriseDescription') }}
          </p>
        </div>
        <button
          class="flex min-h-11 shrink-0 items-center justify-center gap-3 rounded-lg border border-n-slate-5 bg-white px-5 py-2.5 text-sm font-bold text-n-slate-12 hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4"
          @click="
            selectPlan('enterprise', {
              users: 1,
              accounts: 1,
              messages: 0,
              storage: 0,
            })
          "
        >
          {{ t('consult') }}
          <span
            class="i-fluent-arrow-left-24-regular size-4"
            aria-hidden="true"
          />
        </button>
      </div>

      <div class="overflow-hidden rounded-2xl border border-n-slate-4 bg-white">
        <div class="grid lg:grid-cols-[1.4fr_1fr]">
          <div class="p-5 sm:p-8">
            <h3 class="mb-2 text-2xl font-bold leading-relaxed text-n-slate-12">
              {{ t('pricing.calculator') }}
            </h3>
            <p class="mb-6 text-sm leading-7 text-n-slate-11">
              {{ t('pricing.calculatorDescription') }}
            </p>
            <fieldset class="m-0 mb-6 border-0 p-0">
              <legend class="mb-3 text-sm font-bold text-n-slate-12">
                {{ t('pricing.planLabel') }}
              </legend>
              <div class="grid grid-cols-3 gap-2">
                <label
                  v-for="key in calculatorPlans"
                  :key="key"
                  class="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border px-2 text-sm"
                  :class="
                    selectedPlan === key
                      ? 'border-woot-600 bg-woot-25 font-bold text-woot-700'
                      : 'border-n-slate-4 text-n-slate-11'
                  "
                >
                  <input
                    type="radio"
                    name="calculator_plan"
                    :value="key"
                    :checked="selectedPlan === key"
                    class="size-4 accent-woot-600 focus-visible:ring-2 focus-visible:ring-woot-600"
                    @change="changePlan(key)"
                  />{{ planNames[key] }}
                </label>
              </div>
            </fieldset>
            <div class="space-y-5">
              <div
                v-for="field in capacityFields"
                :key="field.key"
                class="grid gap-3 sm:grid-cols-[1fr_11rem] sm:items-start"
              >
                <div>
                  <label
                    :for="`pricing_capacity_${field.key}`"
                    class="text-sm font-bold text-n-slate-12"
                  >
                    {{ field.label }}
                  </label>
                  <p
                    :id="`pricing_hint_${field.key}`"
                    class="mb-0 mt-1 text-xs leading-6 text-n-slate-11"
                  >
                    {{ field.hint }}
                  </p>
                </div>
                <div
                  class="flex items-center gap-1 rounded-lg border border-n-slate-4 bg-white p-1 focus-within:border-woot-600 focus-within:ring-2 focus-within:ring-woot-600/20"
                >
                  <button
                    type="button"
                    :aria-label="
                      t('pricing.decrease', {
                        field: field.label,
                      })
                    "
                    :disabled="field.value <= field.min"
                    class="flex size-10 shrink-0 items-center justify-center rounded-md bg-n-slate-2 text-n-slate-12 hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-woot-600 disabled:cursor-not-allowed disabled:opacity-40"
                    @click="field.step(-1)"
                  >
                    <span
                      class="i-fluent-subtract-24-regular size-4"
                      aria-hidden="true"
                    />
                  </button>
                  <input
                    :id="`pricing_capacity_${field.key}`"
                    :value="number(field.value)"
                    :inputmode="field.inputmode"
                    :aria-describedby="`pricing_hint_${field.key}`"
                    dir="ltr"
                    class="h-10 min-w-0 w-full border-0 bg-transparent px-1 text-center text-base font-bold tabular-nums text-n-slate-12 focus:outline-none"
                    @change="field.set($event.target.value)"
                  />
                  <button
                    type="button"
                    :aria-label="
                      t('pricing.increase', {
                        field: field.label,
                      })
                    "
                    class="flex size-10 shrink-0 items-center justify-center rounded-md bg-n-slate-2 text-n-slate-12 hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-woot-600"
                    @click="field.step(1)"
                  >
                    <span
                      class="i-fluent-add-24-regular size-4"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
              <div class="border-t border-n-slate-4 pt-5">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <label
                    for="pricing_messages"
                    class="text-sm font-bold text-n-slate-12"
                  >
                    {{ t('pricing.messagesLabel') }}
                  </label>
                  <input
                    id="pricing_messages"
                    :value="number(messages)"
                    dir="ltr"
                    inputmode="numeric"
                    aria-describedby="pricing_messages_hint"
                    class="h-11 w-32 rounded-lg border border-n-slate-4 bg-white px-3 text-center text-base font-bold tabular-nums text-woot-700 focus:border-woot-600 focus:outline-none focus:ring-2 focus:ring-woot-600/20"
                    @change="setMessages($event.target.value)"
                  />
                </div>
                <input
                  :value="messages"
                  type="range"
                  :aria-label="t('pricing.messagesLabel')"
                  :aria-valuetext="
                    t('pricing.messageCount', { count: number(messages) })
                  "
                  min="0"
                  max="1000000"
                  step="100"
                  class="mt-4 h-6 w-full cursor-pointer accent-woot-600 focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-woot-600"
                  @input="setMessages($event.target.value)"
                />
                <div class="flex justify-between text-xs text-n-slate-11">
                  <span>{{ number(0) }} </span>
                  <span>{{ number(1000000) }} </span>
                </div>
                <p
                  id="pricing_messages_hint"
                  class="mb-0 mt-3 text-xs leading-6 text-n-slate-11"
                >
                  {{ t('pricing.messagesHint') }}
                </p>
              </div>
            </div>
          </div>
          <aside
            class="flex flex-col border-t border-n-slate-4 bg-woot-25 p-5 sm:p-8 lg:border-s lg:border-t-0"
          >
            <div class="mb-7 flex items-center gap-3">
              <span
                class="flex size-11 items-center justify-center rounded-xl bg-white text-woot-600"
              >
                <span
                  class="i-fluent-receipt-24-regular size-6"
                  aria-hidden="true"
              /></span>
              <div>
                <h4 class="mb-0 text-lg font-bold text-n-slate-12">
                  {{ t('pricing.estimateTitle') }}
                </h4>
                <p class="mb-0 mt-1 text-xs text-n-slate-11">
                  {{ t('pricing.estimatePeriod') }}
                </p>
              </div>
            </div>
            <dl class="mb-6 space-y-4 text-sm text-n-slate-11">
              <div
                v-for="row in estimateRows"
                :key="row.label"
                class="flex items-center justify-between gap-3"
              >
                <dt>{{ row.label }}</dt>
                <dd class="m-0 font-bold tabular-nums text-n-slate-12">
                  {{ row.value }}
                </dd>
              </div>
            </dl>
            <div
              class="mt-auto border-t border-n-slate-5 pt-5"
              role="status"
              aria-atomic="true"
            >
              <p class="mb-1 text-sm text-n-slate-11">
                {{
                  estimate.storageInquiry
                    ? t('pricing.totalWithoutStorage')
                    : t('pricing.total')
                }}
              </p>
              <p
                class="mb-0 text-3xl font-bold leading-relaxed tabular-nums text-woot-700"
              >
                {{
                  number(
                    estimate.storageInquiry
                      ? estimate.totalWithoutStorage
                      : estimate.total
                  )
                }}
                <span class="text-sm font-normal text-n-slate-11">
                  {{ t('pricing.toman') }}
                </span>
              </p>
            </div>
            <p class="mb-5 mt-3 text-xs leading-6 text-n-slate-11">
              {{ t('pricing.estimateNote') }}
            </p>
            <button
              class="min-h-12 w-full rounded-lg bg-woot-600 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-woot-700 focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4"
              @click="requestCapacity"
            >
              {{ t('pricing.select') }}
            </button>
          </aside>
        </div>
      </div>
    </div>
  </section>
</template>
